import { useRouter } from 'next/router';
import { useReduxState } from 'hooks';
import {
  DisplayAttachments,
  ModalButton,
  TextWithLineBreaks,
  Users
} from 'components/common';
import {
  BoxOverflowWrapper,
  BoxTable,
  BoxTableCell,
  BoxTableHeader,
  BoxTableRow
} from 'components/common/box';
import { AttributeDisplayValue } from 'components/common/customAttributes';
import {
  AchievementGroupsOfUsersListActions,
  AchievementRenderAddedBy,
  AchievementUserStatus
} from '.';

import type { GetAchievementGroupCompletionsRes } from 'shared-types/responses';

interface Props {
  groupCompletions: GetAchievementGroupCompletionsRes['groupCompletions'];
  updateGroups: () => Promise<void>;
}

const AchievementGroupsOfUsersList = ({
  groupCompletions,
  updateGroups
}: Props) => {
  const {
    language,
    messages,
    pageState: { achievement }
  } = useReduxState();

  const router = useRouter();

  const handleGroupCompletionClick = (completionId: number) => {
    router.push(
      `/admin/achievements/${achievement.id}` +
        `/group-conversations/${completionId}`
    );
  };

  return (
    <BoxOverflowWrapper>
      <BoxTable>
        <BoxTableHeader>
          <BoxTableCell sticky={true}>{messages.commonUsers}</BoxTableCell>

          <BoxTableCell>{messages.commonAddedBy}</BoxTableCell>

          <BoxTableCell>{messages.commonStatus}</BoxTableCell>

          {achievement.userAttributes?.map((attribute) => (
            <BoxTableCell key={attribute.id}>
              {attribute.titleTranslations?.[language] || attribute.title}
            </BoxTableCell>
          ))}

          <BoxTableCell shrink={true}></BoxTableCell>

          <BoxTableCell
            shrink={true}
            sticky={true}
            stickyAnchor="right"
          ></BoxTableCell>
        </BoxTableHeader>

        {groupCompletions.map((groupCompletion) => (
          <BoxTableRow key={groupCompletion.achievementData.id}>
            <BoxTableCell
              sticky={true}
              stickyBackgroundColor="white"
              style={{ minWidth: 220 }}
            >
              <div
                onClick={() =>
                  handleGroupCompletionClick(groupCompletion.achievementData.id)
                }
                style={{ cursor: 'pointer' }}
              >
                <Users users={groupCompletion.members} />
              </div>
            </BoxTableCell>

            <BoxTableCell>
              <AchievementRenderAddedBy
                addedBy={groupCompletion.achievementData.addedBy}
                enableLink={true}
              />
            </BoxTableCell>

            <BoxTableCell>
              <AchievementUserStatus
                achievementData={groupCompletion.achievementData}
                achievementType={achievement.type}
                numberOfItems={achievement.template.length}
              />
            </BoxTableCell>

            {achievement.userAttributes?.map((attribute) => (
              <BoxTableCell key={attribute.id} style={{ minWidth: 160 }}>
                <AttributeDisplayValue
                  value={
                    groupCompletion.achievementData.userAttributes?.[
                      attribute.id
                    ]
                  }
                />
              </BoxTableCell>
            ))}

            <BoxTableCell
              shrink={true}
              valueContainerStyle={{ justifyContent: 'flex-end' }}
            >
              <div style={{ display: 'flex', gap: 10 }}>
                <DisplayAttachments
                  attachments={groupCompletion.achievementData.attachments}
                  isConfidential={
                    !groupCompletion.achievementData.canSeeAttachments
                  }
                  hideTitle={true}
                  useModalButtonPropsForTable={true}
                />

                {groupCompletion.achievementData.comments ? (
                  <ModalButton
                    category="transparent"
                    icon={['far', 'comment']}
                    iconStyle={{ fontSize: '1.5rem' }}
                    modalProps={{
                      closeButton: true,
                      title: messages.commonComment
                    }}
                    size="small"
                    tooltip={messages.commonComment}
                  >
                    <TextWithLineBreaks
                      text={groupCompletion.achievementData.comments}
                    />
                  </ModalButton>
                ) : null}
              </div>
            </BoxTableCell>

            <BoxTableCell
              valueContainerStyle={{ justifyContent: 'flex-end' }}
              shrink={true}
              sticky={true}
              stickyAnchor="right"
              stickyBackgroundColor="white"
            >
              <AchievementGroupsOfUsersListActions
                groupCompletion={groupCompletion}
                id={`achievement-groups-of-users-list-${groupCompletion.achievementData.id}`}
                updateGroups={updateGroups}
              />
            </BoxTableCell>
          </BoxTableRow>
        ))}
      </BoxTable>
    </BoxOverflowWrapper>
  );
};

export default AchievementGroupsOfUsersList;
